<!--
  택배사 재인증 모달 (전체 화면)

  TODO: 공유 코드 추출 필요 (우선순위: HIGH)
  - 이 파일은 Admin과 Mobile에서 100% 동일합니다 (298줄)
  - farmfree/packages/farmfree-common/shared-components/courier/shared-CourierReauthModal.vue 로 이동 권장

  TODO: 타입 안전성 개선 (우선순위: LOW)
  - line 233: as any → as CourierProviderId로 변경
-->
<template>
  <!-- 전체 화면 모달 (페이지처럼 보임) -->
  <Transition
    enter-active-class="transition-transform duration-300 ease-out"
    enter-from-class="transform translate-x-full"
    enter-to-class="transform translate-x-0"
    leave-active-class="transition-transform duration-300 ease-in"
    leave-from-class="transform translate-x-0"
    leave-to-class="transform translate-x-full"
  >
    <div v-if="isOpen" class="fixed inset-0 z-50 bg-gray-50" @click.self="handleClose">
      <!-- 헤더 -->
      <div class="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
        <button
          @click="handleClose"
          class="p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <Icon name="heroicons:chevron-left" class="w-6 h-6" />
        </button>
        <h1 class="text-lg font-semibold text-gray-900">택배사 재인증</h1>
        <div class="w-10"></div>
      </div>

      <!-- 로딩 중 -->
      <div v-if="isLoading" class="p-4">
        <div class="bg-white rounded-lg shadow-sm p-6 text-center">
          <p class="text-gray-600">계정 정보를 불러오는 중...</p>
        </div>
      </div>

      <!-- 계정 정보 표시 및 재인증 폼 -->
      <div v-else-if="courierAccount" class="p-4 space-y-4">
        <!-- 계정 정보 -->
        <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
          <div class="flex items-center gap-3 pb-3 border-b border-gray-200">
            <span class="text-2xl">{{ providerInfo?.icon }}</span>
            <div>
              <h2 class="font-semibold text-gray-900">{{ providerInfo?.displayName }}</h2>
              <p class="text-sm text-gray-500">{{ courierAccount.accountId }}</p>
            </div>
          </div>
          <div class="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p class="text-sm text-yellow-800 font-medium">⚠️ 토큰이 만료되었습니다</p>
            <p class="text-xs text-yellow-700 mt-1">계속 사용하려면 재인증이 필요합니다.</p>
          </div>
        </div>

        <!-- 재인증 폼 -->
        <div class="bg-white rounded-lg shadow-sm p-4 space-y-4">
          <!-- OTP 입력 (SMS 2FA 필요 시) -->
          <div v-if="otpRequired" class="space-y-4">
            <div class="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p class="text-sm text-blue-800 font-medium">📱 SMS 인증이 필요합니다</p>
              <p class="text-sm text-blue-700">{{ otpMessage }}</p>
            </div>

            <!-- SMS 발송 버튼 (인증정보 파싱 성공 시 표시) -->
            <button
              v-if="parsedOtpInfo"
              @click="handleSendOtpSms"
              :disabled="sendingSms"
              class="w-full bg-blue-600 text-white py-3 rounded-md font-semibold active:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <span v-if="sendingSms">발송 중...</span>
              <template v-else>
                <span>📤</span>
                <span>인증코드 "{{ parsedOtpInfo.authCode }}" 발송</span>
              </template>
            </button>

            <div class="p-4 bg-gray-50 border border-gray-200 rounded-md">
              <p class="text-sm font-medium text-gray-700 mb-2">🔄 자동 인증 확인 중...</p>
              <p class="text-xs text-gray-600">
                문자를 발송하시면 자동으로 인증이 완료됩니다. (3초마다 확인)
              </p>
            </div>
          </div>

          <!-- 재인증 버튼 (OTP 필요 시 숨김) -->
          <button
            v-if="!otpRequired"
            @click="handleReauth"
            :disabled="isAuthenticating"
            class="w-full bg-black text-white py-3 rounded-md font-semibold active:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="isAuthenticating">재인증 중...</span>
            <span v-else>재인증</span>
          </button>

          <!-- 닫기 버튼 -->
          <button
            @click="handleClose"
            :disabled="isAuthenticating"
            class="w-full bg-gray-100 text-gray-700 py-3 rounded-md font-semibold active:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors border border-gray-300"
          >
            나중에 하기
          </button>
        </div>
      </div>

      <!-- 에러 -->
      <div v-else class="p-4">
        <div class="bg-white rounded-lg shadow-sm p-6 text-center">
          <p class="text-red-600">계정을 찾을 수 없습니다.</p>
          <button @click="handleClose" class="mt-4 px-4 py-2 bg-gray-100 rounded-md text-gray-700">
            돌아가기
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onUnmounted, onMounted } from 'vue'
  import { getProviderInfo } from '@farmfree/common/constants/courier/courierProviders'
  import { createCourierSaasFunctionsService } from '@/services/courierSaas/courierSaasFunctionsService'
  import { useDialog } from '@/composables/useDialog'
  import { useCourierSaasStore } from '@/stores'
  import type { CourierSaasAccount } from '@farmfree/common/types/courierSaas/courierSaasAccount'
  import { useCourierOtpPolling, isOtpResponse } from '@/composables/courier/useCourierOtpPolling'
  import { useUnifiedSms } from '@/composables/sms/useUnifiedSms'

  const props = defineProps<{
    isOpen: boolean
    courierId: string
    companyId: string
  }>()

  const emit = defineEmits<{
    close: []
    success: []
  }>()

  const { showAlert } = useDialog()
  const courierSaasStore = useCourierSaasStore()
  const courierSaasFunctionsService = createCourierSaasFunctionsService()
  const { sendSms, sendingSms } = useUnifiedSms()

  const isLoading = ref<boolean>(true)
  const isAuthenticating = ref<boolean>(false)
  const courierAccount = ref<CourierSaasAccount | null>(null)
  const hasHistoryEntry = ref<boolean>(false)

  // OTP 폴링 composable
  const { otpRequired, otpMessage, startOtpPolling, stopOtpPolling, setOtpRequired, reset } =
    useCourierOtpPolling()

  // OTP 메시지에서 인증번호와 수신번호 파싱
  // 예: "핸드폰으로 1522-7513에 "28"를 문자로 보내세요 (제한시간: 3분)"
  const parsedOtpInfo = computed(() => {
    if (!otpMessage.value) return null
    // 패턴: {수신번호(하이픈 포함 가능)}에 "{인증코드}"를
    const match = otpMessage.value.match(/([\d-]{8,13})에\s*"(\d{2})"를/)
    if (match) {
      return {
        targetNumber: match[1].replace(/-/g, ''), // 1522-7513 → 15227513
        authCode: match[2], // 28
      }
    }
    return null
  })

  // 택배사 정보
  const providerInfo = computed(() => {
    if (!courierAccount.value) return null
    return getProviderInfo(courierAccount.value.providerId)
  })

  // 뒤로가기 이벤트 핸들러
  function handlePopState(event: PopStateEvent) {
    // 모달이 열려있고 우리가 추가한 히스토리 엔트리인지 확인
    if (props.isOpen && hasHistoryEntry.value) {
      // 모달만 닫기 (페이지 이동하지 않음)
      hasHistoryEntry.value = false
      stopOtpPolling()
      emit('close')
    }
  }

  // 모달 열릴 때 히스토리에 엔트리 추가
  function addHistoryEntry() {
    if (!hasHistoryEntry.value) {
      // 고유한 state를 가진 히스토리 엔트리 추가
      window.history.pushState({ courierReauthModal: true }, '')
      hasHistoryEntry.value = true
    }
  }

  // 모달 닫힐 때 히스토리 엔트리 제거 (버튼 클릭 등 명시적 닫기)
  function removeHistoryEntry() {
    if (hasHistoryEntry.value) {
      hasHistoryEntry.value = false
      // 현재 히스토리가 모달 엔트리인지 확인
      if (window.history.state?.courierReauthModal) {
        window.history.back()
      }
    }
  }

  // 컴포넌트 마운트 시 popstate 이벤트 리스너 등록
  onMounted(() => {
    window.addEventListener('popstate', handlePopState)
  })

  // 계정 정보 로드
  async function loadAccount() {
    if (!props.courierId || !props.companyId) return

    try {
      isLoading.value = true

      // Store에서 택배사 계정 조회
      const account: CourierSaasAccount | undefined = courierSaasStore.items.find(
        (acc: CourierSaasAccount) => acc.id === props.courierId
      )

      if (!account) {
        await showAlert('계정을 찾을 수 없습니다.')
        handleClose()
        return
      }

      courierAccount.value = account
    } catch (error) {
      console.error('[ReauthModal] 계정 로드 실패:', error)
      await showAlert('계정 정보를 불러오는 중 오류가 발생했습니다.')
    } finally {
      isLoading.value = false
    }
  }

  // 재인증 처리
  async function handleReauth() {
    if (isAuthenticating.value || !courierAccount.value) return

    try {
      isAuthenticating.value = true

      // DB에 저장된 비밀번호를 사용하여 강제 재로그인
      const response = await courierSaasFunctionsService.forceReauth({
        companyId: props.companyId,
        courierId: props.courierId,
      })

      // OTP 필요 응답 처리
      if (!response.isSuccess && isOtpResponse(response.data) && response.data.requiresOtp) {
        console.log('📱 [재인증 모달] OTP 필요 - 자동 폴링 시작')
        setOtpRequired(
          true,
          response.data.message || response.error || 'OTP 인증이 필요합니다.',
          response.data.sessionId || ''
        )
        isAuthenticating.value = false

        // composable의 OTP 폴링 시작
        startOtpPolling({
          companyId: props.companyId,
          courierId: props.courierId,
          providerId: courierAccount.value.providerId as any,
          sessionId: response.data.sessionId || '',
          onSuccess: async () => {
            await showAlert('재인증이 완료되었습니다.')
            emit('success')
          },
          onError: async (msg: string) => {
            await showAlert(msg)
          },
        })
        return
      }

      if (!response.isSuccess) {
        await showAlert(response.error || '재인증 실패')
        return
      }

      // 성공
      await showAlert('재인증이 완료되었습니다.')
      emit('success')
    } catch (error: unknown) {
      console.error('[ReauthModal] 재인증 실패:', error)
      await showAlert('재인증 중 오류가 발생했습니다.')
    } finally {
      isAuthenticating.value = false
    }
  }

  // 닫기 처리
  function handleClose() {
    stopOtpPolling()
    removeHistoryEntry()
    emit('close')
  }

  // SMS 인증코드 발송
  async function handleSendOtpSms() {
    if (!parsedOtpInfo.value) {
      await showAlert('인증 정보를 파싱할 수 없습니다.')
      return
    }

    try {
      const { targetNumber, authCode } = parsedOtpInfo.value
      await sendSms(targetNumber, authCode)
      await showAlert(`인증코드 "${authCode}"를 ${targetNumber}로 발송했습니다.`)
    } catch (error: any) {
      console.error('[ReauthModal] SMS 발송 실패:', error)
      await showAlert(error.message || 'SMS 발송에 실패했습니다.')
    }
  }

  // 모달이 열릴 때 계정 로드
  watch(
    () => props.isOpen,
    (newVal: boolean) => {
      if (newVal) {
        addHistoryEntry()
        loadAccount()
      } else {
        reset()
        courierAccount.value = null
        isLoading.value = true
        // 외부에서 닫힌 경우에도 히스토리 정리
        if (hasHistoryEntry.value) {
          hasHistoryEntry.value = false
        }
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stopOtpPolling()
    window.removeEventListener('popstate', handlePopState)
    // 컴포넌트 언마운트 시 히스토리 정리
    if (hasHistoryEntry.value) {
      hasHistoryEntry.value = false
    }
  })
</script>
